import React, { useState, useEffect } from 'react';
import { Sparkles, Loader2, Lock, HelpCircle, CheckCircle2, Building2, MapPin, Quote, RefreshCw } from 'lucide-react';
import { api } from '../lib/api';
import './Soul.css';

/**
 * SOUL view — brain-layer Slice A, item 4.
 *
 * Read-only "what your brain knows about your company" surface over the nightly
 * synthesis output (tenant_identity). Deliberately honest:
 *   - Below the evidence gate (or no row yet) → a "still learning" state, never a
 *     fabricated profile (the cold-start honesty the Council required).
 *   - Surfaces what is KNOWN *and* what is still UNKNOWN.
 *   - Strictly read-only: no edit controls. The brain is shaped by ingest +
 *     synthesis, not hand-editing.
 */

function asArray(v) {
  return Array.isArray(v) ? v : [];
}

function Confidence({ level }) {
  if (!level) return null;
  return <span className={`soul-confidence soul-confidence-${level}`}>{level} confidence</span>;
}

export default function SoulPage() {
  const [loading, setLoading] = useState(true);
  const [identity, setIdentity] = useState(null);
  const [missing, setMissing] = useState(false);
  const [error, setError] = useState(null);

  async function load() {
    setLoading(true);
    setError(null);
    setMissing(false);
    try {
      const row = await api.get('/memory/identity');
      setIdentity(row);
    } catch (err) {
      // A 404 (no tenant_identity row yet) is the expected "brand-new brain" state,
      // not an error — synthesis simply hasn't run/landed yet. Branch on the
      // structured status code (api.js attaches err.status); fall back to message.
      if (err.status === 404 || /not found|404/i.test(err.message || '')) setMissing(true);
      else setError(err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="soul-page">
        <div className="soul-loading"><Loader2 className="soul-spin" size={28} /> Loading what your brain knows…</div>
      </div>
    );
  }

  const content = (identity && identity.content) || {};
  const status = content.synthesis_status;
  const entryCount = content.entry_count;
  const gate = content.gate;
  // Active when explicitly marked active, OR when there's real synthesized content
  // and it isn't explicitly still-learning — so a row with a summary is never hidden
  // behind the "still learning" state just because its status field is unset.
  const isActive = status === 'active' || (!!content.summary && status !== 'still_learning');
  const stillLearning = missing || !isActive;

  const Header = (
    <div className="soul-header">
      <div className="soul-title">
        <Sparkles size={22} className="soul-title-icon" />
        <h1>What your brain knows</h1>
        <span className="soul-readonly"><Lock size={12} /> read-only</span>
      </div>
      <button className="soul-refresh" onClick={load} title="Refresh"><RefreshCw size={15} /></button>
    </div>
  );

  if (error) {
    return (
      <div className="soul-page">
        {Header}
        <div className="soul-card soul-error">Couldn’t load your brain: {error}</div>
      </div>
    );
  }

  if (stillLearning) {
    const have = Number.isFinite(entryCount) && entryCount >= 0 ? entryCount : 0;
    const need = Number.isFinite(gate) && gate > 0 ? gate : 10;
    const raw = (have / need) * 100;
    const pct = Number.isFinite(raw) ? Math.max(0, Math.min(100, Math.round(raw))) : 0;
    return (
      <div className="soul-page">
        {Header}
        <div className="soul-card soul-learning">
          <h2>Still learning about your company</h2>
          <p className="soul-learning-sub">
            Your brain consolidates what it learns from your connected sources each night. It needs a
            little more to say something confident.
          </p>
          <div className="soul-progress">
            <div className="soul-progress-bar"><div className="soul-progress-fill" style={{ width: `${pct}%` }} /></div>
            <div className="soul-progress-label">{have} / {need} memories gathered</div>
          </div>
          <p className="soul-learning-hint">
            Once enough has been gathered, this page will show what your brain understands about your
            business — and it will stay honest about what it’s still unsure of.
          </p>
        </div>
      </div>
    );
  }

  const known = asArray(content.known);
  const unknowns = asArray(content.unknowns);
  const voice = asArray(identity.voice_samples);
  const biz = identity.business_classification || {};
  const trade = identity.industry_trade_region || {};
  const synthDate = content.synthesized_at ? new Date(content.synthesized_at) : null;
  const synthValid = synthDate && !Number.isNaN(synthDate.getTime());

  return (
    <div className="soul-page">
      {Header}

      {content.summary && (
        <div className="soul-card soul-summary">
          <p>{content.summary}</p>
          <div className="soul-meta">
            {Number.isFinite(entryCount) && <span>{entryCount} memories</span>}
            {synthValid && <span>· updated {synthDate.toLocaleDateString()}</span>}
          </div>
        </div>
      )}

      <div className="soul-grid">
        {(biz.industry || biz.business_type) && (
          <div className="soul-card">
            <div className="soul-card-head"><Building2 size={16} /> Business</div>
            {biz.industry && <div className="soul-kv"><span>Industry</span><strong>{biz.industry}</strong></div>}
            {biz.business_type && <div className="soul-kv"><span>Type</span><strong>{biz.business_type}</strong></div>}
            <Confidence level={biz.confidence} />
          </div>
        )}

        {(trade.trade || trade.region) && (
          <div className="soul-card">
            <div className="soul-card-head"><MapPin size={16} /> Trade &amp; region</div>
            {trade.trade && <div className="soul-kv"><span>Trade</span><strong>{trade.trade}</strong></div>}
            {trade.region && <div className="soul-kv"><span>Region</span><strong>{trade.region}</strong></div>}
            <Confidence level={trade.confidence} />
          </div>
        )}
      </div>

      {known.length > 0 && (
        <div className="soul-card">
          <div className="soul-card-head"><CheckCircle2 size={16} /> What it’s confident about</div>
          <ul className="soul-list">{known.map((k, i) => <li key={i}>{String(k)}</li>)}</ul>
        </div>
      )}

      {unknowns.length > 0 && (
        <div className="soul-card soul-unknowns">
          <div className="soul-card-head"><HelpCircle size={16} /> What it’s still unsure of</div>
          <ul className="soul-list">{unknowns.map((u, i) => <li key={i}>{String(u)}</li>)}</ul>
        </div>
      )}

      {voice.length > 0 && (
        <div className="soul-card">
          <div className="soul-card-head"><Quote size={16} /> Your company’s voice</div>
          <div className="soul-voice">{voice.map((v, i) => <span key={i} className="soul-voice-chip">“{String(v)}”</span>)}</div>
        </div>
      )}
    </div>
  );
}
